import { useRef, useState } from 'react';
import { Box, Flex, Image, Spinner, useDisclosure } from '@chakra-ui/react';
import OutsideClickHandler from 'react-outside-click-handler';
import { isEmpty } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';

import { CloseIcon, NotificationIcon } from 'Asset/icon';
import emptyImage from 'Asset/image/empty-notification.svg';
import { TextBold, TextMedium, TextRegular, TextSemiBold } from 'UiKit/typography';
import { Condition, Iteration } from 'Component/others';
import useGetNotification from 'Domain/notification/useGetNotification';
import HeaderMobile from 'Component/header-mobile';
import useIsMobile from 'Hook/useIsMobile';
import SkeletonComponent from 'UiKit/skeleton';
import usePatchNotificationMarkAllAsRead from 'Domain/notification/usePatchNotificationMarkAllAsRead';
import usePatchNotification from 'Domain/notification/usePatchNotification';

import styles from './styles.module.css';

const Notification = (props) => {
  const isMobile = useIsMobile();
  const contentRef = useRef(null);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [selectedTab, setSelectedTab] = useState('unread');
  const [page, setPage] = useState(1);
  const [previousNotification, setPreviousNotification] = useState([]);
  const [unReadCount, setUnreadCount] = useState(0);

  const { data, isFetching } = useGetNotification({ page, selectedTab }, setPreviousNotification, previousNotification, setUnreadCount);

  const { mutate: mutatePatchNotification } = usePatchNotification();
  const { mutate: mutatePatchBulkNotifications } = usePatchNotificationMarkAllAsRead();
  const isTabLoading = isFetching && page === 1;
  const unReadCountText = unReadCount > 99 ? '99+' : unReadCount;

  const onLoadMore = () => setPage(page + 1);

  const onChangeSwitchTab = (tab) => {
    setSelectedTab(tab);
    setPage(1);
    setPreviousNotification([]);
  };

  const onClickCard = (notification) => {
    mutatePatchNotification({ notification, selectedTab });
    onClose();
  };

  const onClickMarkAllAsRead = () => {
    if (data?.notifications?.length) mutatePatchBulkNotifications();
  };

  return (
    <Box className={styles['notification__container']}>
      <Box className={styles['trigger__button']} {...props} onClick={onOpen} data-testid="notification__button">
        <NotificationIcon />

        <Condition statement={unReadCount}>
          <Box className={styles['notification-count__container']}>{unReadCountText}</Box>
        </Condition>
      </Box>

      <Condition statement={isOpen}>
        <OutsideClickHandler onOutsideClick={onClose}>
          <Box className={styles['notification-content__container']}>
            {/*  Head */}

            <Condition statement={!isMobile} fallback={<HeaderMobile onClick={onClose} type="header-back" title="All notification" />}>
              <Box className={styles['head__section']}>
                <TextBold className={styles['title__text']}>Notifikasi</TextBold>
                <CloseIcon className={styles['close__icon']} size="18px" onClick={onClose} />
              </Box>
            </Condition>

            {/* Filter */}
            <Box className={styles['filter__section']}>
              <Flex className={styles['swith-tab__container']}>
                <Box
                  data-testid="unread__tab"
                  className={`${styles['switch__tab--left']} ${selectedTab === 'unread' ? styles['switch__tab--active'] : ''}`}
                  onClick={() => onChangeSwitchTab('unread')}
                >
                  <TextSemiBold className={styles['tab__text']}>Unread ({unReadCount})</TextSemiBold>
                  <Box className={styles['dot__container']} />
                </Box>

                <Box
                  data-testid="read__tab"
                  className={`${styles['switch__tab--right']} ${selectedTab === 'read' ? styles['switch__tab--active'] : ''}`}
                  onClick={() => onChangeSwitchTab('read')}
                >
                  <TextSemiBold className={styles['tab__text']}> Read</TextSemiBold>
                  <Box className={styles['dot__container']} />
                </Box>
              </Flex>

              <Condition statement={selectedTab === 'unread'}>
                <TextSemiBold
                  data-testid="mark-all__button"
                  onClick={onClickMarkAllAsRead}
                  className={`${styles['mark-all__text']} ${unReadCount ? styles['mark-all__text--active'] : ''}`}
                >
                  Mark all as read
                </TextSemiBold>
              </Condition>
            </Box>

            {/* Content */}
            <Condition statement={isTabLoading}>
              <Iteration
                items={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
                render={(num) => (
                  <Flex className={styles['card__container']} key={num}>
                    <Box className={styles['card-date__section']}>
                      <SkeletonComponent height="20px" width="60%" />
                    </Box>

                    <Box className={styles['card-content__section']}>
                      <SkeletonComponent height="14px" width="60%" mb="4px" />
                      <SkeletonComponent height="12px" width="80%" mb="4px" />
                      <SkeletonComponent height="12px" width="40%" />
                    </Box>
                  </Flex>
                )}
              />
            </Condition>

            <Condition statement={!isTabLoading}>
              <Condition
                statement={!isEmpty(data?.notifications)}
                fallback={
                  <Box className={styles['empty-state__container']}>
                    <Image src={emptyImage} className={styles['empty-state__image']} />
                    <TextBold className={styles['empty-state-title__text']}>Belum ada notifikasi</TextBold>
                    <TextRegular className={styles['empty-state-description__text']}>Cek kembali nanti untuk lihat update terbarumu.</TextRegular>
                  </Box>
                }
              >
                <Box
                  className={styles['content__section']}
                  id="scrollable-parent"
                  data-testid="scrollable-parent"
                  height={selectedTab === 'read' ? 'calc(100vh - 230px)' : 'calc(100vh - 240px)'}
                >
                  <InfiniteScroll
                    scrollableTarget="scrollable-parent"
                    dataLength={data?.notifications}
                    hasMore={page < data?.totalPage}
                    next={onLoadMore}
                    scrollThreshold="80%"
                    loader={
                      <Box className={styles['spinner__container']}>
                        <Spinner size="md" color="var(--color-primary)" />
                      </Box>
                    }
                  >
                    <Box className={styles['content__wrapper']} ref={contentRef}>
                      <Iteration
                        items={data?.notifications}
                        render={(item, index) => (
                          <Flex
                            data-testid={`notification__card--${index}`}
                            className={styles['card__container']}
                            key={index}
                            onClick={() => onClickCard(item)}
                          >
                            <Box className={styles['card-date__section']}>
                              <TextSemiBold className={styles['card-date__text']}>{item?.date}</TextSemiBold>
                              <TextSemiBold className={styles['card-month__text']}>{item?.month}</TextSemiBold>
                            </Box>

                            <Box className={styles['card-content__section']}>
                              <TextMedium className={styles['card-title__text']}>{item?.title}</TextMedium>
                              <Box className={styles['card-description__container']} dangerouslySetInnerHTML={{ __html: item?.description }} />
                            </Box>

                            <Box className={styles['card-time__section']}>
                              <TextMedium className={styles['card-time__text']}>{item?.time}</TextMedium>
                            </Box>
                          </Flex>
                        )}
                      />
                    </Box>
                  </InfiniteScroll>
                </Box>
              </Condition>
            </Condition>
          </Box>
        </OutsideClickHandler>
      </Condition>
    </Box>
  );
};

export default Notification;
